<template>
  <layout>
    <template v-slot:title>Tag Browser</template>
    <div class="form-group">
      <!-- Drop down removed until we decide how we want mult-template functionality to work.  -->
      <!--<drop-down
        id="template"
        label="Template"
        v-model="selectedTemplate"
        :items="templateItems"
      ></drop-down>-->
      <div v-if="selectedTemplate && currentDataContext" class="data-context bold">
        Data Context: {{ currentDataContext }}
      </div>
    </div>
    <div>
      <browser v-if="!!currentSchema" :data-context="currentDataContext" :schema="currentSchema"
        :selected-template="selectedTemplate" @field-selected="fieldSelected">
      </browser>
    </div>
    <template v-slot:modal>
      <insert-list-modal v-if="showInsertListModal && !!selectedField && selectedPath" :field="selectedField"
        :path="selectedPath" @insert="listTypeSelected" @close="showInsertListModal = false" />
      <business-rule-modal v-if="showBusinessRuleModal" :title="businessRuleModalTitle" @continue="ruleSelected"
        @close="ruleSelectCanceled" />
    </template>
  </layout>
</template>
<script>
import { mapState } from "vuex";
import currentTempates from "@/mixins/currentTemplates";
import documentModelMixin from "@/mixins/documentModel";
import templates from "@/services/sharedo/templatesService";
import graph from "@/services/sharedo/graphService";
import tags from "@/services/word/tagsService";
import repeatingSections from "@/services/word/repeatingSectionsService";
import repeatingTables from "@/services/word/repeatingTablesService";
import { ascending } from "@/util/sort";
import Browser from "@/components/tag-browser/Browser.vue";
import InsertListModal from "@/components/tag-browser/InsertListModal.vue";
import BusinessRuleModal from "@/components/business-rules/BusinessRuleModal.vue";
import * as listFieldTypes from "@/constants/listFieldControlTypes";
import * as contentControlType from "@/constants/contentControlTypes";
import contentControls from "@/services/word/contentControlsService";
import {
  Tag,
  RepeatingContentBlock,
  RepeatingTableBlock,
} from "@/models/documentModel";
import { SECTION } from "@/constants/contentControlTypes";

const beforeMount = async function () {
  try {
    this.selectedTemplate = this.currentTemplates[0];

    const templateResults = await templates.getMany(this.currentTemplates);
    templateResults.sort(ascending((t) => t.title));
    this.templateItems = templateResults.map((t) => ({
      label: t.title,
      value: t.systemName,
    }));

    this.logInfo("Loaded templates.", { templates: templateResults });

    this.dataContexts = templateResults.map((t) => ({
      systemName: t.systemName,
      context: t.contextTypeSystemName,
    }));

    this.currentSchema = await graph.getSchemaForType(
      "doc-gen",
      this.selectedTemplate
    );

    this.logInfo("Loaded schema.", { schema: this.currentSchema });
  } catch (error) {
    this.logError("Error loading tag browser.", { error });
  }
};

const fieldSelected = function (args) {
  this.logDebug("Field selected.", args);

  const { field, path } = args;

  this.selectedField = field;
  this.selectedPath = path;

  if (
    field.value.type.kind === "scalar" ||
    field.value.type.kind === "calculated"
  ) {
    this.showBusinessRuleModal = true;
  } else if (field.value.type.kind === "list") {
    this.showInsertListModal = true;
  }
};

const listTypeSelected = function ({ field, type, path }) {
  this.logDebug("List type selected.", { field, type, path });

  this.selectedField = field;
  this.selectedPath = path;
  this.selectedListType = type;

  this.showInsertListModal = false;
  this.showBusinessRuleModal = true;
};

const insertListField = async function (args) {
  this.logDebug("Inserting list field.", args);

  const { path, type, businessRuleId } = args;
  let newSection;

  if (type === listFieldTypes.CONTENT_CONTROL) {
    newSection = await repeatingSections.add(path, this.boundingBoxesHidden);
    this.logDebug("Repeating section inserted.", {
      path,
      contentControl: newSection,
    });
  } else if (type === listFieldTypes.TABLE) {
    newSection = await repeatingTables.add(path, this.boundingBoxesHidden);
    this.logDebug("Repeating table inserted.", {
      path,
      contentControl: newSection,
    });
  }

  const { id, parentId } = newSection;

  const model = this.documentModel;

  let newControl;
  if (args.type === listFieldTypes.CONTENT_CONTROL) {
    newControl = new RepeatingContentBlock(id, path, businessRuleId);
  } else {
    newControl = new RepeatingTableBlock(id, path, businessRuleId);
  }

  if (parentId) {
    const parent = model.findContentControl(parentId);
    parent.contentControls.push(newControl);
  } else {
    model.contentControls.push(newControl);
  }

  await this.saveDocumentModel(model);
  this.logDebug("Document model saved.", model);
};

const ruleSelected = async function (businessRuleId) {
  this.logDebug("Display rule selected.", { displayRuleId: businessRuleId });

  const field = this.selectedField;
  const path = this.selectedPath;
  const type = this.selectedListType;

  if (
    field.value.type.kind === "scalar" ||
    field.value.type.kind === "calculated"
  ) {
    await this.insertTag({ path, businessRuleId });
    this.logDebug("Tag inserted.", { path, displayeRuleId: businessRuleId });
  } else if (field.value.type.kind === "list") {
    await this.insertListField({ field, path, type, businessRuleId });
    this.logDebug("List field inserted.", {
      path,
      type,
      businessRuleId,
    });
  }

  this.showBusinessRuleModal = false;
};

const ruleSelectCanceled = function () {
  this.showBusinessRuleModal = false;
};

const insertTag = async function ({ path, businessRuleId }) {
  this.logDebug("Inserting tag.", { path, displayRuleId: businessRuleId });

  const placeholder = this.currentPlaceholder;

  if (
    placeholder &&
    (placeholder.type === contentControlType.REPEATING_CONTENT_BLOCK ||
      placeholder.type === contentControlType.REPEATING_TABLE_BLOCK)
  ) {
    if (path.startsWith(".")) {
      path = path.substr(1);
    }
  }

  const { id, parentId } = await tags.add(path, this.boundingBoxesHidden);
  this.logDebug("Tag control inserted.", {
    path,
    contentControl: { id, parentId },
  });

  const model = this.documentModel;
  const newTag = new Tag(id, path, businessRuleId);

  if (parentId) {
    const parent = model.findContentControl(parentId);
    parent.contentControls.push(newTag);
  } else {
    model.contentControls.push(newTag);
  }

  await this.saveDocumentModel(model);
  this.logDebug("Document model saved.", model);
};

export default {
  components: { Browser, InsertListModal, BusinessRuleModal },
  mixins: [documentModelMixin, currentTempates],
  data: function () {
    return {
      selectedTemplate: null,
      selectedField: null,
      selectedPath: "",
      selectedListType: "",
      templateItems: [],
      currentSchema: null,
      showInsertListModal: false,
      showBusinessRuleModal: false,
      dataContexts: [],
    };
  },
  computed: {
    ...mapState({
      currentPlaceholder: (state) => state.currentPlaceholder,
      boundingBoxesHidden: (state) => state.boundingBoxesHidden,
    }),
    businessRuleModalTitle: function () {
      const field = this.selectedField;
      if (!!field) {
        const name = field.value.displayName || field.value.name;

        if (
          field.value.type.kind === "scalar" ||
          field.value.type.kind === "calculated"
        ) {
          return `Insert Tag '${name}'`;
        } else if (field.value.type.kind === "list") {
          return `Insert List '${name}'`;
        }
      }

      return "";
    },
    selectedTemplateText: function () {
      const selected = this.templateItems.find(
        (t) => t.value === this.selectedTemplate
      );
      if (selected) {
        return selected.label;
      }

      return "";
    },
    currentDataContext: function () {
      const current = this.dataContexts.find(
        (dc) => dc.systemName === this.selectedTemplate
      );

      return !!current ? current.context : "";
    },
  },
  watch: {
    selectedTemplate: async function (oldValue, newValue) {
      if (oldValue !== newValue) {
        this.currentSchema = await graph.getSchemaForType(
          "doc-gen",
          this.selectedTemplate
        );
      }
    },
    currentPlaceholder: async function (newValue, oldValue) {
      if (newValue || oldValue) {
        if (newValue && newValue.type !== SECTION) {
          const listPlaceholder =
            contentControls.getClosestListPlaceholder(newValue);

          if (listPlaceholder) {
            const paths = [];
            let parent = listPlaceholder.parent;

            while (parent) {
              if (parent.type !== SECTION) {
                paths.push(parent.path);
              }

              parent = parent.parent;
            }

            paths.push(listPlaceholder.path);

            this.currentSchema = await graph.getSchemaForPaths(
              "doc-gen",
              this.selectedTemplate,
              paths
            );
          }
        } else if (oldValue) {
          const listPlaceholder =
            contentControls.getClosestListPlaceholder(oldValue);
          if (listPlaceholder) {
            this.currentSchema = await graph.getSchemaForType(
              "doc-gen",
              this.selectedTemplate
            );
          }
        }
      }
    },
  },
  methods: {
    fieldSelected,
    insertTag,
    insertListField,
    ruleSelected,
    listTypeSelected,
    ruleSelectCanceled,
  },
  beforeMount,
};
</script>
<style lang="scss" scoped>
.data-context {
  font-size: 0.8rem;
}
</style>
